






























































import { defineComponent, onBeforeUnmount, PropType, toRef, unref, watch } from "@vue/composition-api";
import useFullCreatorApplicationUpload from "@/hooks/useFullCreatorApplicationUpload";
import InputWithMentions from "@/components/PostCreate/components/InputWithMetions";
import MainVideo from "@/components/PostCreate/views/MediaPostCreateView/components/MainVideo.vue";
import Participants from "@/components/PostCreate/views/MediaPostCreateView/components/Participants.vue";
import PostThumbnails from "@/components/PostCreate/views/MediaPostCreateView/components/PostThumbnails.vue";
import TrailerVideo from "@/components/PostCreate/views/MediaPostCreateView/components/TrailerVideo.vue";
import ProgressStepsCard from "@/components/ui/ProgressStepsCard";
import useFullCreatorVideoPostController, { FullCreatorPendingFiles } from "../hooks/useFullCreatorVideoPostController";

export default defineComponent({
  name: "VideoPostCard",
  components: {
    Participants,
    ProgressStepsCard,
    MainVideo,
    PostThumbnails,
    TrailerVideo,
    InputWithMentions,
  },
  props: {
    postId: { type: String, required: true },
    userId: { type: String, required: true },
    collapsed: { type: Boolean, default: false },
    pendingFiles: { type: Object as PropType<FullCreatorPendingFiles | null>, default: null },
  },
  setup(props, { emit }) {
    const {
      getVideoController,
      getVideoControllerRetryState,
      isPostPublishing,
      markFileUploaded,
      registerVideoController,
      unregisterVideoController,
    } = useFullCreatorApplicationUpload();
    const existingController = isPostPublishing(props.postId) ? getVideoController(props.postId) : undefined;
    const controller =
      existingController ||
      useFullCreatorVideoPostController(
        {
          postId: toRef(props, "postId"),
          userId: toRef(props, "userId"),
          pendingFiles: toRef(props, "pendingFiles"),
          retryState: getVideoControllerRetryState(props.postId),
        },
        emit,
        { onFileUploaded: markFileUploaded }
      );
    registerVideoController(props.postId, controller);
    onBeforeUnmount(() => unregisterVideoController(props.postId));

    if (!existingController) {
      return {
        ContentFileThumbnailType: controller.ContentFileThumbnailType,
        postData: controller.postData,
        isDraft: controller.isDraft,
        postFileDataList: controller.postFileDataList,
        errorUploadingFile: controller.errorUploadingFile,
        uploadedFilesNumber: controller.uploadedFilesNumber,
        isUploadingFiles: controller.isUploadingFiles,
        description: controller.description,
        localParticipants: controller.localParticipants,
        setParticipants: controller.setParticipants,
        updateParticipant: controller.updateParticipant,
        generateTrailer: controller.generateTrailer,
        trailerRange: controller.trailerRange,
        mainVideos: controller.mainVideos,
        mainVideoLoading: controller.mainVideoLoading,
        showMainVideoRecovery: controller.showMainVideoRecovery,
        mainVideoRecoveryText: controller.mainVideoRecoveryText,
        trailers: controller.trailers,
        sfwThumbnails: controller.sfwThumbnails,
        nsfwThumbnails: controller.nsfwThumbnails,
        cardTitle: controller.cardTitle,
        isPostEmpty: controller.isPostEmpty,
        validationGoal: controller.validationGoal,
        validationCurrent: controller.validationCurrent,
        isComplete: controller.isComplete,
        onAddFiles: controller.onAddFiles,
        onAddFilesFromVault: controller.onAddFilesFromVault,
        onFileRemove: controller.onFileRemove,
        onMainVideoRetry: controller.onMainVideoRetry,
        onTrailerRangeChange: controller.onTrailerRangeChange,
        onTrailerRangeReset: controller.onTrailerRangeReset,
        markMainVideoUploadFailed: controller.markMainVideoUploadFailed,
        waitForUploads: controller.waitForUploads,
        publish: controller.publish,
        remove: controller.remove,
        onCollapseChange: controller.onCollapseChange,
        onMainVideoRemove: controller.onMainVideoRemove,
      };
    }

    watch(
      () => Boolean(unref(controller.isComplete)),
      val => emit("validChange", props.postId, val),
      {
        immediate: true,
      }
    );

    const onCollapseChange = (val: boolean) => {
      if (!val) {
        emit("expand");
      }
    };

    const onMainVideoRemove = async () => {
      try {
        await controller.remove();
        emit("remove", props.postId);
      } catch {
        // Keep the card visible so the user can retry or close it again.
      }
    };

    return {
      ContentFileThumbnailType: controller.ContentFileThumbnailType,
      postData: controller.postData,
      isDraft: controller.isDraft,
      postFileDataList: controller.postFileDataList,
      errorUploadingFile: controller.errorUploadingFile,
      uploadedFilesNumber: controller.uploadedFilesNumber,
      isUploadingFiles: controller.isUploadingFiles,
      description: controller.description,
      localParticipants: controller.localParticipants,
      setParticipants: controller.setParticipants,
      updateParticipant: controller.updateParticipant,
      generateTrailer: controller.generateTrailer,
      trailerRange: controller.trailerRange,
      mainVideos: controller.mainVideos,
      mainVideoLoading: controller.mainVideoLoading,
      showMainVideoRecovery: controller.showMainVideoRecovery,
      mainVideoRecoveryText: controller.mainVideoRecoveryText,
      trailers: controller.trailers,
      sfwThumbnails: controller.sfwThumbnails,
      nsfwThumbnails: controller.nsfwThumbnails,
      cardTitle: controller.cardTitle,
      isPostEmpty: controller.isPostEmpty,
      validationGoal: controller.validationGoal,
      validationCurrent: controller.validationCurrent,
      isComplete: controller.isComplete,
      onAddFiles: controller.onAddFiles,
      onAddFilesFromVault: controller.onAddFilesFromVault,
      onFileRemove: controller.onFileRemove,
      onMainVideoRetry: controller.onMainVideoRetry,
      onTrailerRangeChange: controller.onTrailerRangeChange,
      onTrailerRangeReset: controller.onTrailerRangeReset,
      markMainVideoUploadFailed: controller.markMainVideoUploadFailed,
      waitForUploads: controller.waitForUploads,
      publish: controller.publish,
      remove: controller.remove,
      onCollapseChange,
      onMainVideoRemove,
    };
  },
});
